import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, PlayCircle } from "@phosphor-icons/react";
import { Reveal, Eyebrow, MaskLines } from "../components/Motion";
import { WORK, WORK_FILTERS } from "../constants/site";

const WorkTile = ({ item, index }) => (
  <motion.div
    data-testid={`work-item-${index}`}
    layout
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay: (index % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
    className="group relative mb-4 break-inside-avoid overflow-hidden rounded-sm border border-[color:var(--gold)]/20"
  >
    {item.type === "video" ? (
      <video src={item.src} autoPlay muted loop playsInline className="w-full block transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]" />
    ) : (
      <img src={item.src} alt={item.tag} loading="lazy" className="w-full block transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]" />
    )}
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[color:var(--bg)] via-[color:var(--bg)]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="absolute top-4 left-4">
      <span className="font-mono-x text-[10px] tracking-[0.22em] uppercase text-[color:var(--gold)] glass-nav rounded-full px-3 py-1">{item.cat}</span>
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
      <div className="flex items-center gap-3">
        {item.type === "video" && <PlayCircle size={20} className="text-[color:var(--gold)] shrink-0" weight="fill" />}
        <h3 className="font-display text-xl sm:text-2xl text-[color:var(--text)] leading-tight">{item.tag}</h3>
      </div>
      {item.caption && <p className="text-[color:var(--text-2)] text-sm mt-2 leading-relaxed">{item.caption}</p>}
    </div>
  </motion.div>
);

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const items = filter === "All" ? WORK : WORK.filter((w) => w.group === filter);

  return (
    <div className="pt-36 sm:pt-44">
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8">
        <Eyebrow>Selected work</Eyebrow>
        <h1 className="font-display font-light leading-[0.95] text-[clamp(3rem,9vw,8rem)] text-[color:var(--text)] mt-5">
          <MaskLines lines={["Portfolio"]} delay={0.15} />
        </h1>
        <Reveal delay={0.25}>
          <p className="max-w-2xl text-[color:var(--text-2)] text-lg mt-8 leading-relaxed">
            A living gallery of real client work — event flyers, save-the-dates, invitations, business
            branding and celebration programs — behind the promise of {`"From Stress to Success"`}.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
        {/* Filter tabs */}
        <Reveal>
          <div className="flex flex-wrap gap-3 mb-10" data-testid="portfolio-filters">
            {WORK_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase()}`}
                className={`rounded-full px-6 py-2.5 text-sm tracking-wide border transition-all duration-500 ${
                  filter === f
                    ? "bg-[color:var(--gold)] text-[#0b0710] border-[color:var(--gold)]"
                    : "border-[color:var(--gold)]/25 text-[color:var(--text-2)] hover:border-[color:var(--gold)] hover:text-[color:var(--text)]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <WorkTile key={item.src} item={item} index={i} />
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 text-center">
          <Reveal>
            <h2 className="font-display font-light text-5xl sm:text-6xl text-[color:var(--text)]">
              Let's create <span className="italic text-gradient-gold">yours.</span>
            </h2>
            <Link
              to="/contact"
              data-testid="portfolio-cta"
              className="group inline-flex items-center gap-3 mt-10 rounded-full bg-[color:var(--gold)] px-9 py-4 text-[#0b0710] font-medium transition-all duration-500 hover:bg-[color:var(--gold-light)]"
            >
              Request a Quote
              <ArrowRight className="transition-transform duration-500 group-hover:translate-x-1" size={18} weight="bold" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
